import { ArrowLeft, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

import { getDailyGuidance } from "@/lib/karma/daily";
import {
  ensureStore,
  getMe,
  getMatchById,
  getOtherUserIdFromMatch,
  getUserById,
  listMatchesForUser,
  listMessages,
  sendMessage,
} from "@/lib/karma/storage";

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

export default function Messages() {
  const [storeVersion, setStoreVersion] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMatchId = searchParams.get("match") ?? "";

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);
  const matches = useMemo(() => listMatchesForUser(me.id, store), [me.id, store]);

  const selectedMatch = selectedMatchId ? getMatchById(selectedMatchId, store) : null;
  const selectedOtherUser = useMemo(() => {
    if (!selectedMatch) return null;
    const otherId = getOtherUserIdFromMatch(selectedMatch, me.id);
    return getUserById(otherId, store);
  }, [me.id, selectedMatch, store]);

  const thread = useMemo(() => {
    if (!selectedMatch) return [];
    return listMessages(selectedMatch.id, store);
  }, [selectedMatch, store, storeVersion]);

  const [text, setText] = useState("");

  const daily = useMemo(() => getDailyGuidance(new Date()), []);

  const quickMessages = useMemo(() => {
    return [
      "Merhaba! Tanıştığımıza sevindim 😊",
      `Bugünün enerjisi ${daily.number}: ${daily.title}. Senin günün nasıl gidiyor?`,
      "Numeroloji merakın var mı? Kader/Kalp/İfade sayılarının sende hissettirdiği şey ne?",
    ];
  }, [daily.number, daily.title]);

  const openMatch = (matchId: string) => {
    setSearchParams({ match: matchId });
  };

  const closeThread = () => {
    setSearchParams({});
  };

  const submit = () => {
    if (!selectedMatch) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    sendMessage(selectedMatch.id, trimmed);
    setText("");
    setStoreVersion((v) => v + 1);
  };

  const hasThread = Boolean(selectedMatch && selectedOtherUser);

  return (
    <div className="w-full">
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <div className={hasThread ? "hidden lg:block" : "block"}>
          <div className="space-y-1">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Mesajlar</h1>
            <p className="text-sm text-muted-foreground">Karşılıklı beğeniler eşleşme oluşturur.</p>
          </div>

          <Card className="mt-4 p-4">
            <div className="text-sm font-semibold">Eşleşmeler</div>
            <Separator className="my-3" />

            {matches.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                Henüz eşleşmen yok. Keşif ve Beğeniler sekmelerinden beğeni gönder.
              </div>
            ) : (
              <div className="space-y-2">
                {matches.map((m) => {
                  const otherId = getOtherUserIdFromMatch(m, me.id);
                  const other = getUserById(otherId, store);
                  if (!other) return null;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => openMatch(m.id)}
                      className={
                        "flex w-full items-center justify-between gap-3 rounded-2xl border bg-muted/30 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                      }
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{other.displayName}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {m.lastMessageAt
                            ? `Son mesaj: ${formatTime(m.lastMessageAt)}`
                            : "Yeni eşleşme"}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">›</div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        <div className={hasThread ? "block" : "hidden lg:block"}>
          {hasThread && selectedMatch && selectedOtherUser ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full lg:hidden"
                    onClick={closeThread}
                    aria-label="Geri"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <div className="font-display text-xl font-semibold tracking-tight">
                      {selectedOtherUser.displayName}
                    </div>
                    <div className="text-sm text-muted-foreground">Eşleşme</div>
                  </div>
                </div>
              </div>

              <Card className="p-4">
                <div className="space-y-3">
                  {thread.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      Sohbeti başlatmak için bir mesaj gönder.
                    </div>
                  ) : (
                    <div className="max-h-[55vh] space-y-2 overflow-auto pr-1">
                      {thread.map((m) => {
                        const mine = m.fromUserId === me.id;
                        return (
                          <div
                            key={m.id}
                            className={mine ? "flex justify-end" : "flex justify-start"}
                          >
                            <div
                              className={
                                mine
                                  ? "max-w-[85%] rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground"
                                  : "max-w-[85%] rounded-2xl bg-muted px-4 py-2 text-sm"
                              }
                            >
                              <div>{m.text}</div>
                              <div
                                className={
                                  mine
                                    ? "mt-1 text-right text-[10px] text-primary-foreground/70"
                                    : "mt-1 text-right text-[10px] text-muted-foreground"
                                }
                              >
                                {formatTime(m.createdAt)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Separator />

                  <div className="flex flex-wrap gap-2">
                    {quickMessages.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className="rounded-full border bg-muted/30 px-3 py-1 text-xs text-muted-foreground hover:bg-muted/50"
                        onClick={() => {
                          setText(m);
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <Input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Mesaj yaz…"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                      }}
                    />


                    <Button className="shrink-0 rounded-full" onClick={submit} aria-label="Gönder">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          ) : (
            <Card className="p-5 text-sm text-muted-foreground">
              Bir eşleşme seçince sohbet burada açılır.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
